import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@material-ui/core";
// Import your reusable toolkit components
import {
  ScrollWrapper,
  StyledTable,
  THead,
  TBody,
  Th,
  Td,
  Tr,
  TableSkeleton,
} from "../../components/shared/TableComponents";
import { EmptyState } from "../../components/shared/helpers/StatePrimitives";
import EditIcon from "@mui/icons-material/Edit";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { tokens } from "../../theme/tokens";

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: tokens.color.surface.default,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
  },
  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
  },
  monogram: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    width: "34px",
    height: "34px",
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.color.surface.sunken,
    color: tokens.color.brand.primary,
    fontWeight: 600,
    fontSize: tokens.type.caption.size,
    textTransform: "uppercase",
  },
  clientName: {
    fontWeight: 600,
    color: tokens.color.text.primary,
  },
  clientAbbr: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.text.secondary,
  },
  addressSecondary: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.text.secondary,
  },
  money: {
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
  },
  muted: {
    color: tokens.color.text.secondary,
  },
}));

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("") || "—";

const columns = [
  { label: "Client" },
  { label: "Address" },
  { label: "Hourly", align: "right" },
  { label: "Database", align: "right" },
  { label: "Inv. rate", align: "right" },
  { label: "Inv. mileage", align: "right" },
  { label: "Options", align: "center" },
];

function ClientList({ setInitialValues, clientList, loading }) {
  const classes = useStyles();

  if (!loading && (!clientList || clientList.length === 0)) {
    return (
      <div className={classes.card}>
        <EmptyState
          icon={<BusinessOutlinedIcon />}
          title="No clients yet"
          description="Add a client with the Add Client control above and it will appear here."
        />
      </div>
    );
  }

  const cityLine = (val) =>
    [val.city, [val.state, val.zip_code].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ");

  return (
    <div className={classes.card}>
      <ScrollWrapper>
        <StyledTable>
          <THead>
            <Tr>
              {columns.map((col) => (
                <Th
                  key={col.label}
                  style={col.align ? { textAlign: col.align } : undefined}
                >
                  {col.label}
                </Th>
              ))}
            </Tr>
          </THead>

          <TBody>
            {loading ? (
              <TableSkeleton columns={columns.length} />
            ) : (
              clientList.map((val, key) => (
                <Tr key={val.id || key}>
                  <Td>
                    <div className={classes.clientCell}>
                      <span className={classes.monogram}>
                        {initials(val.client_name)}
                      </span>
                      <div>
                        <div className={classes.clientName}>
                          {val.client_name}
                        </div>
                        {val.abbreviation && (
                          <div className={classes.clientAbbr}>
                            {val.abbreviation}
                          </div>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <div>{val.street_address}</div>
                    {cityLine(val) && (
                      <div className={classes.addressSecondary}>
                        {cityLine(val)}
                      </div>
                    )}
                  </Td>
                  <Td align="right">
                    <span className={classes.money}>${val.hourly_rate}</span>
                  </Td>
                  <Td align="right">
                    <span className={classes.money}>
                      ${val.database_search_rate}
                    </span>
                  </Td>
                  <Td align="right">
                    <span className={classes.money}>
                      ${val.local_investigator_rate}
                    </span>
                  </Td>
                  <Td align="right">
                    <span className={classes.money}>
                      ${val.local_investigator_mileage_rate}
                    </span>
                  </Td>
                  <Td width={60} align="center">
                    <Tooltip title="Edit client">
                      <IconButton
                        size="small"
                        onClick={() => setInitialValues(val)}
                      >
                        <EditIcon sx={{ width: 20, height: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </StyledTable>
      </ScrollWrapper>
    </div>
  );
}

export default ClientList;
